/**
 * Phase 8.12 — messaging client.
 *
 * Owns:
 *
 *   * REST wrappers around /messages, /me/chat-prefs, /friends/blocks.
 *   * Per-conversation WebSocket lifecycle (subscribe / unsubscribe from
 *     /ws/messages/{conversation_id}). Unlike the notifications socket
 *     which is a singleton for the whole session, the messages WS lives
 *     only while a thread view is mounted — a shorter-lived resource
 *     that avoids holding a socket per conversation the user isn't
 *     actively viewing.
 *   * A tiny pub/sub for the Home badge that layers on top of the
 *     existing notifications WS's `chat.unread` events. The badge
 *     subscriber lives in `pages/home.js`.
 *
 * This module deliberately does NOT own the notifications WS —
 * `lib/notifications.js` already covers /ws/notifications. Slice 5
 * routes `chat.unread` events through that socket, so this file just
 * re-broadcasts them to `pages/home.js` via a small state store.
 */

import { api } from '../api.js';
import { auth } from '../auth.js';
import { TrivianaSocket } from '../ws.js';
import { onNotifications } from './notifications.js';

// WS event types — kept in lockstep with services/messaging.py
// WS_TYPE_* + NOTIF_TYPE_CHAT_UNREAD constants.
export const WS_TYPE_MESSAGE_NEW = 'message.new';
export const WS_TYPE_MESSAGE_READ = 'message.read';
export const WS_TYPE_TYPING_START = 'typing.start';
export const WS_TYPE_TYPING_STOP = 'typing.stop';
export const NOTIF_TYPE_CHAT_UNREAD = 'chat.unread';

// REST surface ---------------------------------------------------------------

export function fetchConversations({ before = null, limit = 30 } = {}) {
  const params = new URLSearchParams();
  if (before) params.set('before', before);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return api(`/messages/conversations${qs ? `?${qs}` : ''}`);
}

export function fetchThread(conversationId, { before = null, limit = 50 } = {}) {
  const params = new URLSearchParams();
  if (before) params.set('before', before);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return api(
    `/messages/conversations/${encodeURIComponent(conversationId)}${qs ? `?${qs}` : ''}`,
  );
}

export function markThreadRead(conversationId) {
  return api(
    `/messages/conversations/${encodeURIComponent(conversationId)}/read`,
    { method: 'POST' },
  );
}

export function sendMessage({ toUserId, body, clientUuid }) {
  return api('/messages/send', {
    method: 'POST',
    body: { to_user_id: toUserId, body, client_uuid: clientUuid },
  });
}

export function fetchRequests() {
  return api('/messages/requests');
}

export function acceptRequest(requestId) {
  return api(`/messages/requests/${requestId}/accept`, { method: 'POST' });
}

export function declineRequest(requestId) {
  return api(`/messages/requests/${requestId}/decline`, { method: 'POST' });
}

export function fetchChatPrefs() {
  return api('/me/chat-prefs');
}

export function patchChatPrefs(patch) {
  return api('/me/chat-prefs', { method: 'PATCH', body: patch });
}

export function muteConversation(conversationId, until = null) {
  return api(
    `/messages/conversations/${encodeURIComponent(conversationId)}/mute`,
    { method: 'POST', body: { until } },
  );
}

export function unmuteConversation(conversationId) {
  return api(
    `/messages/conversations/${encodeURIComponent(conversationId)}/mute`,
    { method: 'DELETE' },
  );
}

export function reportMessage(messageId, reason) {
  return api(`/messages/${encodeURIComponent(messageId)}/report`, {
    method: 'POST',
    body: { reason },
  });
}

export function blockUser(userId) {
  return api('/friends/blocks', { method: 'POST', body: { user_id: userId } });
}

// UUID for client-side idempotency on send. Uses `crypto.randomUUID` where
// available (all modern browsers). Falls back to a simple RFC-4122 v4 shim
// for older WebViews so we never accidentally send undefined.
export function messageClientUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Home-badge state -----------------------------------------------------------
//
// The /ws/notifications socket delivers `chat.unread` events with a
// per-conversation count. We accumulate them here and expose a
// subscribe API for pages/home.js so the chat icon badge stays live
// without a REST refresh.

const _unreadByConv = new Map();  // conversationId -> unread_count
const _badgeListeners = new Set();
let _notifUnsub = null;

function _emitBadge() {
  const total = Array.from(_unreadByConv.values()).reduce((a, b) => a + b, 0);
  for (const fn of _badgeListeners) {
    try {
      fn({ total, byConversation: _unreadByConv });
    } catch (err) {
      console.error('messaging badge listener error', err);
    }
  }
}

/**
 * Subscribe to messaging-badge updates. Returns an unsubscribe fn.
 * Event shape: { total, byConversation: Map<convId, unreadCount> }.
 */
export function onChatBadge(fn) {
  _badgeListeners.add(fn);
  // Emit current state immediately so the caller renders without an
  // extra REST prime.
  try {
    fn({ total: getChatUnreadTotal(), byConversation: _unreadByConv });
  } catch (err) {
    console.error('messaging badge listener error', err);
  }
  return () => _badgeListeners.delete(fn);
}

export function getChatUnreadTotal() {
  let total = 0;
  for (const n of _unreadByConv.values()) total += n;
  return total;
}

/**
 * Seed the badge state from a REST snapshot — used on cold home load
 * before the /ws/notifications socket has delivered its first
 * chat.unread event.
 */
export function seedChatBadgeFromInbox(items) {
  _unreadByConv.clear();
  for (const item of items || []) {
    if (item && item.id && typeof item.unread_count === 'number') {
      if (item.unread_count > 0) {
        _unreadByConv.set(item.id, item.unread_count);
      }
    }
  }
  _emitBadge();
}

/**
 * Boot the messaging badge subscription. Idempotent — safe to call from
 * `main.js` on every auth change. Depends on `lib/notifications.js`
 * having its socket up (chat.unread events arrive via /ws/notifications).
 *
 * Also REST-primes the badge on every call so unread messages from
 * PRIOR sessions surface immediately on Home. WS `chat.unread` events
 * only fire on NEW messages — without the prime, the badge stayed at
 * zero on cold load even for users with a pile of unread threads,
 * until they either visited /messages (which seeds via the inbox
 * fetch) or a new message came in.
 */
async function _primeBadgeFromInbox() {
  if (!auth.isAuthenticated()) return;
  try {
    const res = await fetchConversations();
    seedChatBadgeFromInbox((res && res.items) || []);
  } catch {
    // Best-effort — a WS `chat.unread` event will reconcile on the
    // next incoming message, and the /messages page seeds on visit.
  }
}

export function ensureChatBadgeStarted() {
  if (!_notifUnsub) {
    _notifUnsub = onNotifications((event) => {
      if (event.kind === 'chat_unread') {
        const { conversationId, unreadCount } = event;
        if (unreadCount > 0) {
          _unreadByConv.set(conversationId, unreadCount);
        } else {
          _unreadByConv.delete(conversationId);
        }
        _emitBadge();
      }
    });
  }
  // Prime unconditionally so a re-login as a different user pulls the
  // new user's unread state (and clears the previous account's).
  void _primeBadgeFromInbox();
}

export function stopChatBadge() {
  if (_notifUnsub) {
    _notifUnsub();
    _notifUnsub = null;
  }
  _unreadByConv.clear();
  _emitBadge();
}

// Per-thread WebSocket helper -----------------------------------------------

/**
 * Open a `/ws/messages/{conversationId}` socket. Returns a small handle
 * whose lifetime the page controls — call `close()` when the thread
 * view unmounts.
 *
 *   const chan = openThreadSocket(convId, {
 *     onMessageNew: (msg) => ...,
 *     onMessageRead: ({ user_id, last_read_at }) => ...,
 *     onTyping: ({ user_id, type }) => ...,
 *   });
 *   // later:
 *   chan.startTyping();
 *   chan.stopTyping();
 *   chan.ping();
 *   chan.close();
 */
export function openThreadSocket(
  conversationId,
  { onMessageNew, onMessageRead, onTyping } = {},
) {
  const ws = new TrivianaSocket(
    `/ws/messages/${encodeURIComponent(conversationId)}`,
  );
  let pingTimer = null;

  ws.on('message', (msg) => {
    if (!msg || typeof msg !== 'object') return;
    switch (msg.type) {
      case WS_TYPE_MESSAGE_NEW:
        if (onMessageNew) onMessageNew(msg.message);
        break;
      case WS_TYPE_MESSAGE_READ:
        if (onMessageRead) onMessageRead(msg);
        break;
      case WS_TYPE_TYPING_START:
      case WS_TYPE_TYPING_STOP:
        if (onTyping) onTyping(msg);
        break;
      default:
        break;
    }
  });

  ws.on('open', () => {
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      if (ws) ws.send({ type: 'ping' });
    }, 25_000);
  });

  ws.on('close', () => {
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  });

  ws.connect();

  return {
    startTyping: () => ws.send({ type: WS_TYPE_TYPING_START }),
    stopTyping: () => ws.send({ type: WS_TYPE_TYPING_STOP }),
    ping: () => ws.send({ type: 'ping' }),
    close: () => {
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      ws.close();
    },
  };
}
